import { z } from 'zod'
import {
  DEFAULT_CATALOG,
  catalogDefinitionSchema,
  catalogFingerprint,
  catalogViewSchema,
  draftSchema,
  publicationSchema,
  publishedCatalogSchema,
  type CatalogDefinition,
  type CatalogView,
  type PublishedCatalog,
} from './catalogModel'

export const CATALOG_STORAGE_PREFIX = 'ail.show.catalog.v1:'
export interface CatalogScope {
  locationId: string
  actorId: string
  role: string
}
export type CatalogCommand = { requestId: string; expectedRevision: number } & (
  { type: 'save-draft'; baseVersionId: string; definition: CatalogDefinition } | { type: 'publish' }
)
const storeSchema = z
  .strictObject({
    locationId: z.string(),
    revision: z.number().int().nonnegative(),
    activeId: z.string(),
    versions: z.record(z.string(), publishedCatalogSchema),
    draft: draftSchema.nullable(),
    publications: z.array(publicationSchema),
    receipts: z.record(z.uuid(), z.string()),
  })
  .superRefine((state, ctx) => {
    const versions = Object.entries(state.versions)
    if (
      versions.length > 100 ||
      !state.versions['2'] ||
      !state.versions[state.activeId] ||
      versions.some(
        ([id, v]) => v.formVersionId !== id || (id !== '2' && v.locationId !== state.locationId),
      ) ||
      new Set(state.publications.map((p) => p.formVersionId)).size !== versions.length - 1 ||
      state.publications.length !== versions.length - 1 ||
      state.publications.some((p) => !state.versions[p.formVersionId]) ||
      (state.publications.at(-1)?.formVersionId ?? '2') !== state.activeId ||
      (state.draft && state.draft.baseVersionId !== state.activeId)
    )
      ctx.addIssue({ code: 'custom', message: 'Widersprüchlicher Vorlagenspeicher.' })
  })
type Store = z.infer<typeof storeSchema>

/** Browser-only adapter. The API serializes writes/signing with the branch workspace lock. */
export function createCatalogRepository(storage: Pick<Storage, 'getItem' | 'setItem'>) {
  const read = (locationId: string): Store => {
    const raw = storage.getItem(CATALOG_STORAGE_PREFIX + locationId)
    if (raw === null)
      return {
        locationId,
        revision: 0,
        activeId: '2',
        versions: { '2': DEFAULT_CATALOG },
        draft: null,
        publications: [],
        receipts: {},
      }
    try {
      const state = storeSchema.parse(JSON.parse(raw))
      if (state.locationId !== locationId) throw new Error('scope')
      return state
    } catch {
      throw new Error(
        'Die gespeicherten Vorlagen sind nicht lesbar. Sie wurden nicht überschrieben.',
      )
    }
  }
  const view = (scope: CatalogScope): CatalogView => {
    const state = read(scope.locationId)
    return catalogViewSchema.parse({
      locationId: state.locationId,
      revision: state.revision,
      active: state.versions[state.activeId],
      ...(scope.role === 'branch_manager'
        ? { draft: state.draft, publications: state.publications }
        : {}),
    })
  }
  return {
    read: view,
    assertVersion(locationId: string, catalog: PublishedCatalog, active = false) {
      const state = read(locationId)
      if (
        catalogFingerprint(state.versions[catalog.formVersionId]) !== catalogFingerprint(catalog) ||
        (active && catalog.formVersionId !== state.activeId)
      )
        throw new Error('Die Anamnesefassung wurde geändert. Bitte öffnen Sie die Anamnese erneut.')
    },
    write(
      scope: CatalogScope,
      actorName: string,
      command: CatalogCommand,
      now = new Date().toISOString(),
    ): CatalogView {
      if (scope.role !== 'branch_manager')
        throw new Error('Nur die Standortleitung darf Vorlagen bearbeiten.')
      z.uuid().parse(command.requestId)
      z.number().int().nonnegative().parse(command.expectedRevision)
      const state = read(scope.locationId)
      const digest = catalogFingerprint({ command, actorId: scope.actorId })
      if (state.receipts[command.requestId]) {
        if (state.receipts[command.requestId] !== digest)
          throw new Error('Dieser Vorgang wurde bereits anders verwendet.')
        return view(scope)
      }
      if (state.revision !== command.expectedRevision)
        throw new Error('Die Vorlage wurde inzwischen geändert. Bitte laden Sie sie neu.')
      const actor = { id: scope.actorId, name: actorName }
      if (command.type === 'save-draft') {
        if (command.baseVersionId !== state.activeId)
          throw new Error('Die Vorlage wurde inzwischen veröffentlicht. Bitte laden Sie sie neu.')
        state.draft = {
          definition: catalogDefinitionSchema.parse(command.definition),
          baseVersionId: state.activeId,
          savedAt: now,
          actor,
        }
      } else {
        if (!state.draft) throw new Error('Bitte speichern Sie zuerst einen Entwurf.')
        if (Object.keys(state.versions).length >= 100)
          throw new Error('Die maximale Anzahl an Demonstrationsvorlagen wurde erreicht.')
        const id = crypto.randomUUID()
        state.versions[id] = publishedCatalogSchema.parse({
          ...state.draft.definition,
          formVersionId: id,
          versionLabel: `Anamnesebogen · ${id}`,
          locationId: scope.locationId,
        })
        state.activeId = id
        state.publications.push({ formVersionId: id, publishedAt: now, actor })
        state.draft = null
      }
      state.revision++
      state.receipts[command.requestId] = digest
      storage.setItem(
        CATALOG_STORAGE_PREFIX + scope.locationId,
        JSON.stringify(storeSchema.parse(state)),
      )
      return view(scope)
    },
  }
}
